<template>
  <div class="load-simulation-data">
    <accordion-panel
      id="load-simulation-data__accordion"
      :collapsed.sync="collapsed"
      @shown="onChangeCollapsed"
    >
      <template #title>
        <label class="m-0">
          {{ $t('engineering.load-simulation.graphic-representation-points') }}
        </label>
      </template>
      <div class="pt-2 pb-1">
        <div
          v-if="pointsList.length"
          class="load-simulation-data__subtitle-wrapper mb-2"
        >
          <div
            class="load-simulation-data__subtitle-content d-inline-block mr-5 mb-2"
            v-for="(subtitle, index) in subtitles"
            :key="index"
          >
            <div class="d-inline-block">
              <i
                class="fas fa-square-full mr-2"
                aria-hidden="true"
                :class="subtitle.class"
              ></i> {{ $t(`fields.engineering.load-simulation.${subtitle.text}`) }}
            </div>
          </div>
        </div>
        <div class="load-simulation-data__chart mb-2">
          <div v-if="pointsList.length">
            <e-chart
              class="load-simulation-data__chart-echarts"
              autoresize
              ref="loadSimulationDataChart"
              @rendered="onRendered"
            />
            <img
              class="load-simulation-data__chart-image img-fluid w-100"
              alt="chart-image"
              :src="chartImg"
            >
          </div>
          <s-empty-list
            v-else
            class="p-5"
            size="md"
            :empty-message="$t('engineering.load-simulation.empty-list')"
            :empty-description="$t('engineering.load-simulation.empty-description')"
          />
        </div>
      </div>
    </accordion-panel>
  </div>
</template>
<script>
import themeService from '@/services/themeService';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import AccordionPanel from '@/components/AccordionPanel';
import { formatNumber } from '@/services/translate';
import { mapGetters } from 'vuex';

const UPDATE_LISTENER_TIMEOUT = 1000;

export default {
  name: 'LoadSimulationDataChart',
  components: {
    AccordionPanel,
  },
  data: vm => ({
    collapsed: false,
    updateListener: null,
    seriesNames: [
      vm.$t('fields.engineering.load-simulation.environment-temperature'),
      vm.$t('fields.engineering.load-simulation.loading'),
    ],
    chartImg: null,
  }),
  computed: {
    ...mapGetters([
      'simulationData',
    ]),
    pointsList() {
      if (this.simulationData.isIntervalInvalid) return [];
      return this.simulationData.validPoints || [];
    },
    series() {
      const environmentTemperatureSeries = [];
      const loadingsSeries = [];

      (this.pointsList || []).forEach((item) => {
        const { dateTime, environmentTemperature, loading } = item;

        environmentTemperatureSeries.push({
          x: dateTime,
          y: environmentTemperature,
        });

        loadingsSeries.push({
          x: dateTime,
          y: loading,
        });
      });

      return [
        environmentTemperatureSeries,
        loadingsSeries,
      ];
    },
    subtitles() {
      return [
        {
          class: 'blue-line',
          text: 'environment-temperature',
        },
        {
          class: 'green-line',
          text: 'loading',
        },
      ];
    },
  },
  mounted() {
    this.updateChartSeries(true);
  },
  methods: {
    getTooltipFormatter(params) {
      return `
        ${params.marker} ${params.seriesName}<br/>
        ${params.value[0]} (${this.$n(params.value[1])})
      `;
    },
    formatX(item) {
      return [dateTimeFormatter(item.x, formats.CHART), item.y];
    },
    getChartSeries() {
      const series = [];
      const showSymbol = series?.length < 2;
      (this.series || []).forEach((serie, index) => {
        series.push({
          name: this.seriesNames[index],
          type: 'line',
          smooth: false,
          data: (serie || []).map(item => this.formatX(item)),
          lineStyle: {
            shadowColor: 'rgba(0, 0, 0, 0.15)',
            shadowBlur: 2,
            shadowOffsetY: 3,
          },
          itemStyle: {
            borderWidth: 1,
          },
          showSymbol,
        });
      });
      return series;
    },
    getOptions() {
      return {
        backgroundColor: themeService.getStyleVar('color-background-dark'),
        color: [themeService.getStyleVar('chart-color-blue'), themeService.getPrimaryColor()],
        tooltip: {
          trigger: 'item',
          confine: true,
          formatter: this.getTooltipFormatter,
          textStyle: {
            color: 'white',
          },
          backgroundColor: 'rgba(110, 110, 110, 0.85)',
          axisPointer: {
            type: 'cross',
            label: {
              color: themeService.getStyleVar('color-page-background'),
              formatter: (value) => {
                if (value.axisDimension === 'y') {
                  return formatNumber(value.value,
                    { minimumFractionDigits: 1, maximumFractionDigits: 3 });
                }
                return value.value.replace('\n', ' ');
              },
            },
          },
        },
        legend: {
          show: false,
        },
        xAxis: {
          type: 'category',
          axisTick: {
            alignWithLabel: true,
          },
          axisLabel: {
            lineHeight: 15,
          },
          axisLine: {
            onZero: false,
            lineStyle: {
              color: themeService.getFontColor(),
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: themeService.getFontColor(),
            },
          },
          splitNumber: 3,
          axisLabel: {
            formatter: '{value}',
            showMinLabel: false,
          },
          splitLine: {
            show: false,
          },
        },
        grid: {
          top: 30,
          bottom: 50,
          right: 60,
          left: 60,
        },
        series: this.getChartSeries(),
        style: {
          border: '1px solid red',
        },
      };
    },
    callDelegateMethod() {
      const options = this.getOptions();

      if (this.$refs.loadSimulationDataChart?.delegateMethod) {
        this.$refs.loadSimulationDataChart
          .delegateMethod('setOption', options);
      }
    },
    updateChartSeries(immediate = false) {
      if (immediate) {
        this.callDelegateMethod();
        return;
      }

      clearTimeout(this.updateListener);

      this.updateListener = setTimeout(() => {
        this.callDelegateMethod();
      }, UPDATE_LISTENER_TIMEOUT);
    },
    onChangeCollapsed() {
      if (!this.collapsed) this.updateChartSeries(true);
    },
    onRendered() {
      this.$nextTick(() => {
        this.chartImg = this.$refs.loadSimulationDataChart.getDataURL();
      });
    },
  },
  watch: {
    pointsList() {
      this.updateChartSeries();
    },
  },
};

</script>
<style lang="sass" scoped>
.load-simulation-data
  border: 1px solid var(--color-table-header-bg)
  border-radius: 4px

  & .green-line,
  & .blue-line
    font-size: 1.1em

  & .green-line
    color: var(--color-primary)

  & .blue-line
    color: var(--chart-color-blue)

  &__chart
    overflow-x: auto
    background-color: var(--color-background-dark)
    border: 1px solid var(--color-border-dark)
    border-radius: 5px

    & .load-simulation-data__chart-image
      display: none

    ::v-deep
      .echarts
        min-width: 100%

    @media print
      & .load-simulation-data__chart-echarts
        display: none

      & .load-simulation-data__chart-image
        display: block
</style>
